.button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-4);
  border: 1px solid transparent;
  font-family: var(--font-family);
  font-weight: var(--text-weight-medium);
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  box-sizing: border-box;
  /* Focus ring is a 1px outset box-shadow (per Figma "Focus" state) so it
   * layers cleanly over the variant's background without pushing layout.
   * We animate box-shadow + background-color + border-color + filter. */
  transition:
    background-color var(--motion-duration-fast) ease,
    border-color var(--motion-duration-fast) ease,
    box-shadow var(--motion-duration-fast) ease,
    color var(--motion-duration-fast) ease,
    filter var(--motion-duration-fast) ease;
}

.button:focus-visible {
  outline: none;
  /* 1px outset ring matching Figma's "Focus" state. Uses --text-primary
   * because it swaps light/dark automatically — neutral-600 in light,
   * near-white in dark — which is exactly what the Figma ring calls for. */
  box-shadow: 0 0 0 1px var(--text-primary);
}

.button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.label {
  /* Label is a span so leading/trailing icons align on the button's
   * central axis regardless of text wrapping. min-width:0 lets flex
   * shrinking work if the parent is narrow. */
  min-width: 0;
}

.fullWidth {
  width: 100%;
}

/* ─── Sizes ──────────────────────────────────────────────────────── */

/* xs — the tiny "Default" variant in Figma. 12px text, 14px icon,
 * rounded-12, narrower vertical padding. */
.size-xs {
  padding: var(--spacing-2) var(--spacing-8);
  border-radius: var(--radius-12);
  font-size: var(--text-size-xs);
  line-height: var(--text-lh-xs);
  letter-spacing: var(--text-xs-tracking);
  min-height: 24px;
}

/* s — rounded-8, 14px text, 16px icon. */
.size-s {
  padding: var(--spacing-4) var(--spacing-8);
  border-radius: var(--radius-8);
  font-size: var(--text-size-s);
  line-height: var(--text-lh-s);
  min-height: 28px;
}

/* m — the default size. Fixed 40px tall per Figma (h-[40px] p-[8px]). */
.size-m {
  padding: var(--spacing-8);
  border-radius: var(--radius-8);
  font-size: var(--text-size-s);
  line-height: var(--text-lh-s);
  height: 40px;
}

/* ─── Variants ───────────────────────────────────────────────────── */

/* Primary — the vertical cyan→green brand gradient, via --brand-gradient
 * in tokens-extensions.css. Shared with the bottom nav bar's active-tab
 * indicator, so the value has exactly one definition.
 *
 * Both the gradient and its ink are theme-invariant. Figma ships identical
 * stops for light and dark, and the wash is light in both — so the label
 * must stay dark. `var(--text-primary)` would resolve to #f5f5f5 on dark,
 * giving near-white text on a light gradient at roughly 2.2:1, well below
 * WCAG AA's 4.5:1. That is why --brand-gradient-ink is a fixed value and
 * not a theme-aware token.
 *
 * Waiting on: design ratifying a gradient variable (`Gradient/1`) in Figma.
 * When that lands, BOTH consumers here and in BottomNavBar switch to the
 * ratified name and the two overlay entries are deleted — see the cleanup
 * obligation in tokens-extensions.css. */
.variant-primary {
  background: var(--brand-gradient);
  color: var(--brand-gradient-ink);
  border-color: transparent;
  /* The base .button has `border: 1px solid transparent` so variants with
   * real borders (outline) don't shift size against variants without. For
   * gradient backgrounds, though, the default `background-origin: padding-box`
   * means the gradient's 0%/100% stops anchor at the padding edges — so
   * the 1px transparent border on top/bottom shows as a visible strip of
   * either the parent background or the extended edge color. Forcing
   * `background-origin: border-box` makes the gradient fill the full
   * border-box cleanly with no strips. */
  /* background-origin: border-box is the sole fix — it anchors the gradient
   * stops at the border edge rather than the padding edge, eliminating the
   * visible strip at the top/bottom of the 1px transparent border. The default
   * value of background-clip is already border-box so no declaration needed. */
  background-origin: border-box;
}
.variant-primary:hover:not(:disabled) {
  filter: brightness(0.95);
}
.variant-primary:active:not(:disabled) {
  filter: brightness(0.9);
}

/* Secondary — neutral fill, primary text. No dedicated hover token
 * in the Figma file for Secondary (the "hover" variant isn't defined
 * in the component set) so we fall back to a subtle brightness bump. */
.variant-secondary {
  background: var(--button-secondary-bg-default);
  color: var(--text-primary);
  border-color: transparent;
}
.variant-secondary:hover:not(:disabled) {
  background: var(--button-secondary-bg-hover);
}

/* Outline — bg-primary fill, neutral border. Hover paints in a subtle
 * background via --button-outline-bg-hover (Figma defines this explicitly). */
.variant-outline {
  background: var(--bg-primary);
  color: var(--text-primary);
  border-color: var(--button-outline-stroke);
}
.variant-outline:hover:not(:disabled) {
  background: var(--button-outline-bg-hover);
}

/* Ghost — no background, no border, primary text. Hover paints the same
 * subtle fill as Outline. */
.variant-ghost {
  background: transparent;
  color: var(--text-primary);
  border-color: transparent;
}
.variant-ghost:hover:not(:disabled) {
  background: var(--button-outline-bg-hover);
}

/* Negative — destructive fill, on-dark text. */
.variant-negative {
  background: var(--button-negative-bg);
  color: var(--text-on-dark);
  border-color: transparent;
}
.variant-negative:hover:not(:disabled) {
  filter: brightness(0.95);
}
.variant-negative:active:not(:disabled) {
  filter: brightness(0.9);
}

/* Negative-ghost — demoted destructive: red label, no fill, no border.
 * Hover paints the same subtle fill as Outline/Ghost rather than a red
 * wash, so the control stays quiet right up to the click. */
.variant-negative-ghost {
  background: transparent;
  color: var(--text-negative);
  border-color: transparent;
}
.variant-negative-ghost:hover:not(:disabled) {
  background: var(--button-outline-bg-hover);
}

/* Loading — keep the label visible (so the button width doesn't jump)
 * but swap the leading icon slot for the spinner. The button itself is
 * disabled via the `disabled` attribute set on the <button>, so no
 * pointer interaction. */
.loading {
  cursor: wait;
}
